/**
 * LP2 Product Purchase Section – Interactive JS
 *
 * Handles:
 *  1. Swiper init (main carousel + thumbnail sync)
 *  2. Size selector toggle
 *  3. Bundle tier selection (updates price display + variant ID)
 *  4. Add-on checkboxes
 *  5. Buy Now → AJAX /cart/add.js → dispatches ajaxProduct:added to open side cart
 *  6. Accordion expand/collapse
 */

import Swiper from "swiper";
import { Navigation, Thumbs } from "swiper/modules";

Swiper.use([Navigation, Thumbs]);

// How long the cart must have no writes queued or in flight before BUY NOW goes
// to checkout, and the most it will ever wait.
const CHECKOUT_SETTLE_MS = 600;
const CHECKOUT_WAIT_MAX_MS = 1200;

(function () {
  const section = document.querySelector("[data-lp2-product-section]");
  if (!section) return;

  // -------------------------------------------------------
  // 1. Swiper: Main + Thumbs
  // -------------------------------------------------------
  let thumbSwiper = null;

  const thumbEl = section.querySelector("[data-lp2-thumb-carousel]");
  const mainEl = section.querySelector("[data-lp2-main-carousel]");

  if (thumbEl) {
    thumbSwiper = new Swiper(thumbEl, {
      slidesPerView: 5,
      spaceBetween: 8,
      watchSlidesProgress: true,
      breakpoints: {
        0: { slidesPerView: 4, spaceBetween: 6 },
        769: { slidesPerView: 5, spaceBetween: 8 },
      },
    });
  }

  if (mainEl) {
    // eslint-disable-next-line no-unused-vars
    const slideCount = mainEl.querySelectorAll(".swiper-slide").length;
    // eslint-disable-next-line no-unused-vars
    const mainSwiper = new Swiper(mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slideCount > 2,
      autoHeight: true,
      observer: true,
      observeParents: true,
      navigation: {
        prevEl: section.querySelector("[data-lp2-main-prev]"),
        nextEl: section.querySelector("[data-lp2-main-next]"),
      },
      thumbs: thumbSwiper ? { swiper: thumbSwiper } : undefined,
    });
  }

  // -------------------------------------------------------
  // 2. Size Selector
  // -------------------------------------------------------
  let selectedSize = "1";
  let selectedColor = "1";
  const sizeBtns = section.querySelectorAll("[data-size]");
  const colorBtns = section.querySelectorAll("[data-color]");

  function updateAddonPrices() {
    let checkboxes = section.querySelectorAll(".lp2-product__addon-checkbox");
    checkboxes.forEach(function (cb) {
      let label = cb.closest(".lp2-product__addon");
      if (!label) return;
      let priceEl = label.querySelector("[data-lp2-addon-price]");
      let compareEl = label.querySelector("[data-lp2-addon-compare]");
      let price =
        cb.getAttribute("data-addon-price-size-" + selectedSize) || "";
      let compare =
        cb.getAttribute("data-addon-compare-size-" + selectedSize) || "";
      if (priceEl) priceEl.textContent = price;
      if (compareEl) {
        compareEl.textContent = compare;
        compareEl.style.display = compare ? "" : "none";
      }
    });
  }

  // Price display: elements + updater declared before the size selector below,
  // which calls updatePriceDisplay() on size change to re-render per-size prices.
  const priceCurrentEl = section.querySelector("[data-lp2-price-current]");
  const priceCompareEl = section.querySelector("[data-lp2-price-compare]");
  const priceSavingsEl = section.querySelector("[data-lp2-price-savings]");

  function updatePriceDisplay(tier) {
    // Per-size prices (optional) override the tier's base price for sizes 2+
    const currentPrice =
      tier.getAttribute("data-price-current-size-" + selectedSize) ||
      tier.getAttribute("data-price-current") ||
      "";
    const comparePrice =
      tier.getAttribute("data-price-compare-size-" + selectedSize) ||
      tier.getAttribute("data-price-compare") ||
      "";
    const savings = tier.getAttribute("data-savings") || "";

    if (priceCurrentEl) priceCurrentEl.textContent = currentPrice;
    if (priceCompareEl) {
      priceCompareEl.textContent = comparePrice;
      priceCompareEl.style.display = comparePrice ? "" : "none";
    }
    if (priceSavingsEl) {
      priceSavingsEl.textContent = savings;
      priceSavingsEl.style.display = savings ? "" : "none";
    }
  }

  sizeBtns.forEach(function (btn) {
    btn.addEventListener("click", function () {
      sizeBtns.forEach(function (b) {
        b.classList.remove("is-active");
      });
      btn.classList.add("is-active");
      selectedSize = btn.getAttribute("data-size");
      updateAddonPrices();
      // Re-render prices for the selected tier (per-size prices, if configured)
      const selectedTierEl = section.querySelector(
        "[data-lp2-tier].is-selected",
      );
      if (selectedTierEl) updatePriceDisplay(selectedTierEl);
    });
  });

  // -------------------------------------------------------
  // 2b. Color Selector (optional — only present when the section
  //     has color options configured)
  // -------------------------------------------------------
  const colorNameEl = section.querySelector("[data-lp2-color-name]");

  colorBtns.forEach(function (btn) {
    btn.addEventListener("click", function () {
      colorBtns.forEach(function (b) {
        b.classList.remove("is-active");
      });
      btn.classList.add("is-active");
      selectedColor = btn.getAttribute("data-color");
      if (colorNameEl) {
        colorNameEl.textContent = btn.getAttribute("data-color-name") || "";
      }
    });
  });

  // -------------------------------------------------------
  // 3. Bundle Tier Selection
  // -------------------------------------------------------
  const tiers = section.querySelectorAll("[data-lp2-tier]");

  function selectTier(tier) {
    tiers.forEach(function (t) {
      t.classList.remove("is-selected");
    });
    tier.classList.add("is-selected");
    updatePriceDisplay(tier);
  }

  tiers.forEach(function (tier) {
    tier.addEventListener("click", function () {
      selectTier(tier);
    });
  });

  // -------------------------------------------------------
  // 4. Get selected variant + addons
  // -------------------------------------------------------
  function getSelectedTier() {
    return section.querySelector("[data-lp2-tier].is-selected");
  }

  function getSelectedVariantId() {
    const tier = getSelectedTier();
    if (!tier) return null;
    // Colors 2–6 use their own variant attributes. A blank one returns null
    // so the "configure variant IDs" guard blocks the add instead of silently
    // adding the color-1 (base) variant. Sections without a color selector
    // never leave color 1, so they still use the base variant.
    if (selectedColor !== "1") {
      return (
        tier.getAttribute(
          "data-variant-size-" + selectedSize + "-color-" + selectedColor,
        ) || null
      );
    }
    return tier.getAttribute("data-variant-size-" + selectedSize);
  }

  function getSelectedQuantity() {
    const tier = getSelectedTier();
    if (!tier) return 1;
    return parseInt(tier.getAttribute("data-quantity"), 10) || 1;
  }

  function getAddonItems() {
    const items = [];
    const checkboxes = section.querySelectorAll(
      ".lp2-product__addon-checkbox:checked",
    );
    checkboxes.forEach(function (cb) {
      const variantId = cb.getAttribute(
        "data-addon-variant-size-" + selectedSize,
      );
      const addonQty =
        parseInt(cb.getAttribute("data-addon-quantity"), 10) || 1;
      if (variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: addonQty,
        });
      }
    });
    return items;
  }

  // -------------------------------------------------------
  // 5. Buy Now → AJAX Cart → Open Side Cart
  // -------------------------------------------------------
  const buyBtn = section.querySelector("[data-lp2-buy-now]");
  if (buyBtn) {
    buyBtn.addEventListener("click", function () {
      const variantId = getSelectedVariantId();
      const quantity = getSelectedQuantity();

      if (!variantId) {
        // If no variant ID configured, show gentle message
        // This happens during development before variant IDs are set
        // eslint-disable-next-line no-alert
        alert(
          "Please configure variant IDs in the theme editor for this bundle tier.",
        );
        return;
      }

      // Build items array
      let items = [
        {
          id: parseInt(variantId, 10),
          quantity: quantity,
        },
      ];

      // Add any checked add-ons
      const addonItems = getAddonItems();
      items = items.concat(addonItems);

      // Disable button during request
      buyBtn.disabled = true;
      buyBtn.textContent = "ADDING...";

      const cartAddUrl =
        (window.theme && window.theme.routes && window.theme.routes.cartAdd) ||
        "/cart/add.js";

      fetch(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error("Cart add failed: " + response.status);
          }
          return response.json();
        })
        .then(function (product) {
          // Dispatch event for any listeners (e.g. analytics, side cart)
          document.dispatchEvent(
            new CustomEvent("ajaxProduct:added", {
              detail: {
                product: product,
                addToCartBtn: buyBtn,
              },
            }),
          );

          // Loading /checkout saves the cart itself, so a tracking write still
          // in flight (Triple Whale's __ref_id, which reacts to this add) would
          // be lost. Let the cart write queue go quiet first, capped so
          // checkout is never blocked. See snippets/cart-write-queue.liquid.
          buyBtn.textContent = "REDIRECTING...";
          const queue = window.cartWriteQueue;
          (queue ? queue.idle(CHECKOUT_SETTLE_MS, CHECKOUT_WAIT_MAX_MS) : Promise.resolve()).then(function () {
            window.location.href = "/checkout";
          });
        })
        .catch(function (err) {
          console.error("LP2 Product: Cart add error", err);
          buyBtn.disabled = false;
          buyBtn.textContent = "BUY NOW";
          // eslint-disable-next-line no-alert
          alert("Something went wrong. Please try again.");
        });
    });

    // Store original text
    buyBtn.dataset.originalText = buyBtn.textContent.trim();
  }

  // -------------------------------------------------------
  // 6. Accordion Expand/Collapse
  // -------------------------------------------------------
  const accordionTriggers = section.querySelectorAll(
    "[data-lp2-accordion-trigger]",
  );
  accordionTriggers.forEach(function (trigger) {
    trigger.addEventListener("click", function () {
      const accordion = trigger.closest("[data-lp2-accordion]");
      if (!accordion) return;

      const isOpen = accordion.classList.contains("is-open");

      // Close all
      section.querySelectorAll("[data-lp2-accordion]").forEach(function (acc) {
        acc.classList.remove("is-open");
        const icon = acc.querySelector(".lp2-product__accordion-icon");
        if (icon) icon.textContent = "+";
      });

      // Toggle current
      if (!isOpen) {
        accordion.classList.add("is-open");
        const icon = accordion.querySelector(".lp2-product__accordion-icon");
        if (icon) icon.textContent = "−";
      }
    });
  });
})();
